import React, { useEffect, useState } from "react";
import {Button, Col, Form, Row} from "react-bootstrap";
import {StudentGroup, StudentStore} from "Stores/studentStore";
import {SimpleCheckbox} from "Components/Common/SimpleCheckbox";
import {
    ColumnDef,
    SortingState,
} from "@tanstack/react-table";
import {CommonStore, Student, StudentsDataQuery} from "Stores/commonStore";
import {inject, observer} from "mobx-react";

import {AppStore} from "Stores/appStore";
import {Defaults, Utils} from "../../common/utils";
import {Committee, CommitteeStore} from "Stores/committeeStore";
import {SelectWrapper} from "Components/Common/SelectWrapper";
import UserSelector from "Components/Common/UserSelector";
import {User} from "Stores/userStore";
import {FeatureGatingStore} from "Stores/featureGating";
import { StyledTable } from "Components/Common/StyledTable";
import DebouncedInput from "Components/Common/DebouncedInput";

interface StudentGroupFormProps {
    onSubmit?: (group: StudentGroup, students: Student[], users: string[]) => void
    onChange?: (group: StudentGroup, students: Student[], users: string[]) => void
    onCancel: () => void
    className?: string
    group?: StudentGroup
    studentStore?: StudentStore
    appStore?: AppStore
    commonStore?: CommonStore
    committeeStore?: CommitteeStore
    featureGatingStore?: FeatureGatingStore
    theme?: string
    groupType?: string
}

interface StudentGroupFormState {
    isActive: boolean
    name: string
    description: string
    adminsCanView: boolean
    facultyCanView: boolean
    mentorsCanView: boolean
    publicCanView: boolean
    studentGroupType: string
    showCurrentStudents: boolean
    students: Student[]
    selected: Student[]
    saved: Student[]
    committees: Committee[]
    selectedCommittee: Committee | null
    selectedUsers: string[]
    loading: boolean
    pages: number
    pageSize: number
    pageNumber: number
    showFilters: boolean

}

export const StudentGroupForm = inject("studentStore", "appStore", "commonStore", "committeeStore", "featureGatingStore")
    (observer((props: StudentGroupFormProps) => {

        const [state, setState] = useState<StudentGroupFormState>({
            isActive: props.group ? props.group.is_active : true,
            name: props.group ? props.group.name : "",
            description: props.group && props.group.description ? props.group.description : "",
            adminsCanView: props.group ? props.group.admins_can_view : props.groupType !== "PERSONAL",
            facultyCanView: props.group ? props.group.faculty_can_view : props.groupType !== "PERSONAL",
            mentorsCanView: props.group ? props.group.mentors_can_view : props.groupType !== "PERSONAL",
            publicCanView: props.group ? props.group.public_can_view : props.groupType !== "PERSONAL",
            studentGroupType:  props.group ? props.group.group_type : (props.groupType === "PERSONAL" ? "PERSONAL" : "EVERYONE"),
            showCurrentStudents: !!props.group,
            loading: false,
            pages: 0,
            committees: [],
            selectedCommittee: null,
            selectedUsers: [],
            pageSize: Defaults.tablePageSize(),
            pageNumber: Defaults.tablePage(),
            students: [] as Student[],
            selected: [] as Student[],
            saved: [] as Student[],
            showFilters: !props.group
        });

        const handleStateChange = (field: keyof StudentGroupFormState, value: any) => {
            setState((prev) => ({
                ...prev,
                [field]: value,
            }));
        };

        const isRowSelected = (row: Student): boolean => {
            const found = state.selected.find((student: Student) => student.ruuid === row.ruuid);
            return !!found;
        };

        const onSelect = (row: Student): void => {
            let selected = state.selected.slice();
            if (! isRowSelected(row)) {
                selected.push(row);
            } else {
                selected = state.selected.filter((student: Student) => student.ruuid !== row.ruuid);
            }
            setState(prev => ({ ...prev, selected }));
        };
// !
        const getColumns = () => {
            const columns: ColumnDef<Student>[] = [
                {
                    id: "selector",
                    header: "In group",
                    cell: props => {
                        const onChange = () => {
                             onSelect(props.row.original);
                        };

                        return (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <SimpleCheckbox
                                    id={`student-group-form-students-${props.row.original.ruuid}`}
                                    onChange={onChange}
                                    isChecked={ isRowSelected(props.row.original)}
                                />
                            </div>
                        );
                    },
                    size: 60,
                    meta: {
                        style: {
                            textAlign: 'center',
                            verticalAlign: 'middle'
                        }
                    }
                },
                {
                    header: "First Name",
                    accessorKey: "first_name",
                },
                {
                    header: "Last Name",
                    accessorKey: "last_name"
                },
                {
                    header: "Student ID",
                    accessorKey: "schools_student_id",
                },
                {
                    header: "Email",
                    accessorKey: "student_email"
                },
                // NOTE: The status column was removed as part of task EAI-5019
                // {
                //     id: "status",
                //     header: "Status",
                //     accessorKey: "status",
                //     size: 100,
                // },
            ];

            return columns;
        };

        const onChangeGroupType = (groupType: string) => {
            if (groupType === "EVERYONE") {
                 setState(prev => ({...prev, selectedCommittee: null, selectedUsers: [], studentGroupType: groupType }));
            } else if (groupType === "LIMITED_ACCESS") {
                 setState(prev => ({...prev, studentGroupType: groupType, publicCanView: false }));
            } else {
                 setState(prev => ({...prev, studentGroupType: groupType }));
            }
        };

        const isValid = (): boolean => {
            const name = state.name.trim();
            return name.length > 0;
        };

        const handleSubmit = () => {
            if ( isValid()) {
                const group: StudentGroup = {
                    ruuid:  props.group ? props.group.ruuid : "",
                    is_active:  state.isActive,
                    name:  state.name,
                    description:  state.description,
                    admins_can_view:  state.adminsCanView,
                    faculty_can_view:  state.facultyCanView,
                    mentors_can_view:  state.mentorsCanView,
                    public_can_view:  state.publicCanView,
                    group_type:  state.studentGroupType,
                    view_committee_id:  state.selectedCommittee ? state.selectedCommittee.id : null,
                    school_id:  props.group ? props.group.school_id : ""
                };

                if ( props.onSubmit) {
                     props.onSubmit(group, state.selected, state.selectedUsers);
                }
            }
        };

        const handleOnChange = () => {
            const group: StudentGroup = {
                ruuid:  props.group ? props.group.ruuid : "",
                is_active:  state.isActive,
                name:  state.name,
                description:  state.description || "",
                admins_can_view:  state.adminsCanView,
                faculty_can_view:  state.facultyCanView,
                mentors_can_view:  state.mentorsCanView,
                public_can_view:  state.publicCanView,
                group_type:  state.studentGroupType,
                view_committee_id:  state.selectedCommittee ? state.selectedCommittee.id : null,
                school_id:  props.group ? props.group.school_id : ""
            };

            if ( props.onChange) {
                props.onChange(group, state.selected, state.selectedUsers);
            }
        };

        const handleCancel = () => {
             props.onCancel();
        };

        const handleShowCurrent = () => {
            if (!state.showCurrentStudents) {
                setState(prev => ({
                    ...prev,
                    showCurrentStudents: true,
                    students:  prev.saved.slice(),
                    showFilters: false
                }));
            }
        };

        const handleShowAll = () => {
            if (!props.commonStore) {
                return null;
            }

            if (state.showCurrentStudents) {
                setState(prev => ({
                    ...prev,
                    showCurrentStudents: false,
                    showFilters: true
                }));
            }
        };

        const renderAllStudentsTable = () => {
            if (!props.studentStore || !props.commonStore || state.showCurrentStudents) {
                return null;
            }
            return <AllStudentsListReactTable columns={ getColumns()} studentStore={ props.studentStore} commonStore={ props.commonStore} />;
        };

        const renderCurrentStudentsTable = () => {
            if (!state.showCurrentStudents) {
                return null;
            }
            return <CurrentStudentsListReactTable columns={ getColumns()} data={ state.saved} />;
        };

        const getInitialData = () => {
            if (props.studentStore && props.group) {
                 setState(prev => ({...prev, loading: true }));
                 props.studentStore.fetchStudentsForGroup( props.group.ruuid).then((students: Student[]) => {
                    if (!students) {
                        return null;
                    }
                     setState(prev => ({...prev, students: students, selected: students, saved: students, loading: false }));
                });
            } else {
                 setState(prev => ({...prev, students: [], selected: [], saved: [] }));
            }

            if ( props.committeeStore) {
                 props.committeeStore.fetchCommittees().then((committees: Committee[]) => {
                     setState(prev => ({ ...prev, committees }));
                     if (props.group) {
                        const found = committees.find(committee => committee.id === props.group?.view_committee_id);
                        if (found) {
                             setState(prev => ({...prev, selectedCommittee: found }));
                        }
                    }
                });
            }

            if ( props.commonStore && props.group && props.group.group_type !== "PERSONAL") {
                 props.commonStore.fetchUsersFromStudentGroup( props.group.ruuid).then((users: User[]) => {
                    setState(prev => ({...prev, selectedUsers: users.map(user => user.ruuid) }));
                });
            }
        };

        useEffect(() => {
            getInitialData();
        }, [props]);

        useEffect(() => {
            handleOnChange();
        }, [ 
                state.name, 
                state.description, 
                state.isActive, 
                state.adminsCanView, 
                state.facultyCanView, 
                state.mentorsCanView, 
                state.publicCanView, 
                state.studentGroupType, 
                state.selectedCommittee, 
                state.selectedUsers, 
                state.selected
            ]);

        return (
            <Form className={"student-group-form " + (props.theme ?? "")}>
                <div className="form-section-group">
                    <div className="mb-3 form-section">
                        <div className="title-form-section">Status</div>
                        <Form.Group controlId="formStudentGroupStatus">
                            <SimpleCheckbox
                                id={`student-group-form-${props.group ? props.group.ruuid : ""}`}
                                onChange={(next: boolean) => handleStateChange("isActive", next)}
                                isChecked={state.isActive}
                                label="Active"
                            />
                        </Form.Group>
                    </div>
                    <div className="form-section-bordered">
                        <Form.Group as={Col} controlId="formStudentGroupName">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                className="student-group-input"
                                type="text"
                                value={state.name}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    handleStateChange("name", event.target.value)
                                }
                                placeholder="Insert Student Group Name"
                            />
                        </Form.Group>

                        <Form.Group as={Col} controlId="exampleForm.formStudentGroupDescription">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                className="student-group-input"
                                as="textarea"
                                value={state.description}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    handleStateChange("description", event.target.value)
                                }
                                placeholder="Insert Student Group Description"
                                rows={1}
                            />
                        </Form.Group>
                    </div>
                </div>
                {props.groupType !== "PERSONAL"
                    && Utils.isFeatureActive("ui", "student_groups_permissions", props.featureGatingStore!.featureGatingList) && (
                    <div className="form-section-group">
                        <div className="form-section" style={{ gridColumn: "1 / -1", display: "block" }}>
                            <div className="title-form-section">Permissions</div>
                            <Row className="mb-3 pt-2">
                                <Col xs={9} style={{ paddingTop: ".25rem" }}>
                                    <Row>
                                        <Col xs={2}><span>Type:</span></Col>
                                        <Col xs={3} as={Form.Group} controlId={"student-group-everyone" + (props.group ? props.group.ruuid : "")}>
                                            <Form.Check
                                                label="Everyone"
                                                checked={state.studentGroupType === "EVERYONE"}
                                                onChange={() => onChangeGroupType("EVERYONE")}
                                                type="radio"
                                            />
                                        </Col>
                                        <Col xs={4} as={Form.Group} controlId={"student-group-limited-access" + (props.group ? props.group.ruuid : "")}>
                                            <Form.Check
                                                label="Limited Access"
                                                checked={state.studentGroupType === "LIMITED_ACCESS"}
                                                onChange={() => onChangeGroupType("LIMITED_ACCESS")}
                                                type="radio" />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={5} className="ps-5">
                                    <SimpleCheckbox
                                        onChange={(next: boolean) => handleStateChange("adminsCanView", next)}
                                        isChecked={state.adminsCanView}
                                        disabled={state.studentGroupType === "EVERYONE"}
                                        label="Admins" />
                                    <SimpleCheckbox
                                        onChange={(next: boolean) => handleStateChange("facultyCanView", next)}
                                        isChecked={state.facultyCanView}
                                        disabled={state.studentGroupType === "EVERYONE"}
                                        label="Faculty (Users with Courses)" />
                                    <SimpleCheckbox
                                        onChange={(next: boolean) => handleStateChange("mentorsCanView", next)}
                                        isChecked={state.mentorsCanView}
                                        disabled={state.studentGroupType === "EVERYONE"}
                                        label="Mentors (Users with Mentees)" />
                                </Col>
                                <Col xs={5}>
                                    <Form.Group className="mb-1">
                                        <Form.Label>Committee</Form.Label>
                                        <SelectWrapper
                                            getOptionLabel={(committee: Committee) => committee.name}
                                            isClearable={true}
                                            placeholder="Committee"
                                            onChange={(next: Committee | null) => handleStateChange("committees", next)}
                                            isDisabled={state.studentGroupType === "EVERYONE"}
                                            options={state.committees}
                                            value={state.selectedCommittee} />
                                    </Form.Group>
                                    <Form.Group className="mb-1">
                                        <Form.Label>Users</Form.Label>
                                        <UserSelector
                                            isMulti={true}
                                            isDisabled={state.studentGroupType === "EVERYONE"}
                                            selectedUsers={state.selectedUsers}
                                            onChange={users => handleStateChange("selectedUsers" , users)} />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    </div>
                )}
                <div className="students-list-actions form-section-group">
                    <div className="form-section">
                        <div className="title-form-section">Students</div>
                        {props.group && (
                            <div className="actions-student-group">
                                <Button variant="outline-light" className={state.showCurrentStudents ? "active" : ""} onClick={handleShowCurrent}>
                                    current students
                                </Button>
                                <Button variant="outline-light" className={!state.showCurrentStudents ? "active" : ""} onClick={handleShowAll}>
                                    all students
                                </Button>
                            </div>
                        )}
                        { renderAllStudentsTable()}
                        { renderCurrentStudentsTable()}
                    </div>
                </div>
                { props.onSubmit &&
                    <Row className="mb-3 pt-3">
                        <Col className="text-end button-group">
                            <Button
                                onClick={handleCancel}
                                variant="outline"
                                className="cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={! isValid()}
                                variant="secondary"
                            >
                                Save Changes
                            </Button>
                        </Col>
                    </Row>
                }
            </Form>
        );
}));

interface CurrentStudentsListReactTableProps {
    columns: ColumnDef<Student>[]
    data: Student[]
}

function CurrentStudentsListReactTable({ columns, data }: CurrentStudentsListReactTableProps) {
    const [pageSize, setPageSize] = useState(Defaults.tablePageSize());

    return (
        <div className="current-students-table-container">
            <StyledTable
                columns={columns}
                defaultData={data}
                className="current-students-table"
                pageSize={pageSize}
                setPageSize={setPageSize}
            />
        </div>
    );
}

interface StudentsListReactTableProps {
    columns: ColumnDef<Student>[],
    studentStore?: StudentStore,
    commonStore?: CommonStore
}

function AllStudentsListReactTable({ columns, commonStore }: StudentsListReactTableProps) {
    const [dataQuery, setDataQuery] = React.useState<StudentsDataQuery | null>(null);
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [pageSize, setPageSize] = React.useState<number>(Defaults.tablePageSize());
    const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(0);
    const [totalPagesNumber, setTotalPagesNumber] = React.useState<number>(1);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [searchBarValue, setSearchBarValue] = React.useState<string>("");
    const [selectedStatus] = React.useState<string>("ALL");

    React.useEffect(() => {
        if (commonStore) {
            setLoading(true);
            let sortBy;
            let sortOrder;
            if (sorting.length > 0) {
                sortBy = sorting[0].id;
                sortOrder = sorting[0].desc ? "desc" : "asc";
            }

            const filters: Record<string, any> = {};
            if (selectedStatus !== "ALL") {
                if (selectedStatus === "ACTIVE") {
                    filters["status"] = { operator: "eq", value: "Active" };
                } else if (selectedStatus === "Unknown") {
                    filters["status"] = { operator: "eq", value: "Unknown" };
                } else {
                    filters["status"] = [
                        { operator: "distinct", value: "Active" },
                        { operator: "distinct", value: "Inactive" },
                    ];
                }
            }

           if (searchBarValue.trim()) {
                filters["search_term"] = {
                    operator: "contains",
                    value: `${searchBarValue}`,
                };
            }

            commonStore.fetchStudents({
                page: currentPageNumber + 1,
                pageSize: pageSize,
                sortBy: sortBy,
                sortOrder: sortOrder,
                filters: filters,
            }).then((students) => {
                setLoading(false);
                setDataQuery({
                    data: students,
                    result: { totalCount: commonStore.totalStudents }
                });

                const totalPages = Math.ceil(commonStore.totalStudents / pageSize);
                setTotalPagesNumber(totalPages);
            });
        }
    }, [currentPageNumber, pageSize, sorting, searchBarValue, selectedStatus]);

    const handleSearchChange = (value: string) => {
        setSearchBarValue(value.toString());
        setCurrentPageNumber(0);
    };

    return (
      <div className="all-students-table-container">
        <div className="header-students">
          <div
            className="search-bar"
          >
            <DebouncedInput
              type="text"
              value={searchBarValue}
              onChange={handleSearchChange}
              placeholder="Search by Name, Last Name, Student ID or Email"
              className="search-input"
            />
            <span
            >
              <i
                className={"type icon icon-search search-icon-bar"}
                aria-hidden="true"
                data-testid="icon-search"
              ></i>
            </span>
          </div>
        </div>
          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              <StyledTable
                  columns={columns}
                  defaultData={dataQuery?.data ?? []}
                  className="all-students-table"
                  totalPagesNumber={totalPagesNumber}
                  currentPageNumber={currentPageNumber}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  setCurrentNumber={setCurrentPageNumber}
                  setSorting={setSorting}
                  sorting={sorting}
                  noDataMessage="No students found"
            isLoading={loading}
            showRowsPerPage={true}
          />
        </div>
      </div>
    );
}